import { Chip, Tooltip, Box, Typography } from "@mui/material";
import {
  Done as DoneIcon,
  WarningAmber,
  BuildCircle,
  RocketLaunch,
} from "@mui/icons-material";
import Table from "../Table/Table";

/**
 * Tooltip content for build status
 */
const BuildTooltipContent = ({ buildCounter, reason, latestBuildReason, message }) => (
  <Box sx={{ p: 0.5 }}>
    <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
      Build #{buildCounter || "N/A"}
    </Typography>
    <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.8)" }}>
      Reason: {latestBuildReason || reason || "N/A"}
    </Typography>
    {message && (
      <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.6)", mt: 0.5, maxWidth: 250 }}>
        {message}
      </Typography>
    )}
  </Box>
);

/**
 * Tooltip content for runtime status
 */
const RuntimeTooltipContent = ({ pods, latestImage, serviceName }) => {
  // Extract build number from image tag if available (e.g., image:1 -> build #1)
  const buildNumber = latestImage?.match(/:(\d+)@sha256/)?.[1] ||
                      latestImage?.match(/:(\d+)$/)?.[1] ||
                      "N/A";
  // Extract short SHA
  const shortSha = latestImage?.match(/@sha256:([a-f0-9]{12})/)?.[1] ||
                   latestImage?.match(/sha256:([a-f0-9]{12})/)?.[1] ||
                   "N/A";

  return (
    <Box sx={{ p: 0.5 }}>
      <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
        Running Build #{buildNumber}
      </Typography>
      <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.8)" }}>
        Pods: {pods || 0}
      </Typography>
      {shortSha !== "N/A" && (
        <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.6)", fontFamily: "monospace" }}>
          SHA: {shortSha}...
        </Typography>
      )}
      {serviceName && (
        <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.6)", mt: 0.5 }}>
          Service: {serviceName}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Build status chip for table
 */
const BuildStatusChip = ({ status, reason, buildCounter, latestBuildReason, message }) => {
  const isBuilding = reason === "BuildRunning" || reason === "BuilderNotReady";
  const isReady = status === "True";

  const chip = isBuilding ? (
    <Chip
      size="small"
      icon={<BuildCircle sx={{ fontSize: "0.9rem !important", color: "#f59e0b !important" }} />}
      label="Building"
      sx={{
        height: 22,
        fontSize: "0.7rem",
        fontWeight: 600,
        background: "rgba(245, 158, 11, 0.12)",
        color: "#f59e0b",
        border: "none",
        cursor: "pointer",
      }}
    />
  ) : isReady ? (
    <Chip
      size="small"
      icon={<DoneIcon sx={{ fontSize: "0.9rem !important", color: "#22c55e !important" }} />}
      label="Ready"
      sx={{
        height: 22,
        fontSize: "0.7rem",
        fontWeight: 500,
        background: "rgba(34, 197, 94, 0.12)",
        color: "#22c55e",
        border: "none",
        cursor: "pointer",
      }}
    />
  ) : (
    <Chip
      size="small"
      icon={<WarningAmber sx={{ fontSize: "0.9rem !important", color: "#ef4444 !important" }} />}
      label="Failed"
      sx={{
        height: 22,
        fontSize: "0.7rem",
        fontWeight: 500,
        background: "rgba(239, 68, 68, 0.12)",
        color: "#ef4444",
        border: "none",
        cursor: "pointer",
      }}
    />
  );

  return (
    <Tooltip
      title={<BuildTooltipContent buildCounter={buildCounter} reason={reason} latestBuildReason={latestBuildReason} message={message} />}
      arrow
      placement="top"
    >
      {chip}
    </Tooltip>
  );
};

/**
 * Runtime status chip for table
 */
const RuntimeStatusChip = ({ status, pods, latestImage, serviceName }) => {
  const getChip = () => {
    if (status === "deploying") {
      return (
        <Chip
          size="small"
          icon={<RocketLaunch sx={{ fontSize: "0.9rem !important", color: "#F97316 !important" }} />}
          label="Deploying"
          sx={{
            height: 22,
            fontSize: "0.7rem",
            fontWeight: 600,
            background: "rgba(234, 88, 12, 0.12)",
            color: "#F97316",
            border: "none",
            cursor: "pointer",
          }}
        />
      );
    }

    if (status === "running") {
      return (
        <Chip
          size="small"
          icon={<DoneIcon sx={{ fontSize: "0.9rem !important", color: "#22c55e !important" }} />}
          label={pods > 0 ? `${pods} pod${pods !== 1 ? "s" : ""}` : "Running"}
          sx={{
            height: 22,
            fontSize: "0.7rem",
            fontWeight: 500,
            background: "rgba(34, 197, 94, 0.12)",
            color: "#22c55e",
            border: "none",
            cursor: "pointer",
          }}
        />
      );
    }

    if (status === "error") {
      return (
        <Chip
          size="small"
          icon={<WarningAmber sx={{ fontSize: "0.9rem !important", color: "#ef4444 !important" }} />}
          label="Error"
          sx={{
            height: 22,
            fontSize: "0.7rem",
            fontWeight: 500,
            background: "rgba(239, 68, 68, 0.12)",
            color: "#ef4444",
            border: "none",
            cursor: "pointer",
          }}
        />
      );
    }

    // Scaled to zero - service exists but no pods (Knative serverless idle state)
    if (status === "scaled") {
      return (
        <Chip
          size="small"
          icon={<DoneIcon sx={{ fontSize: "0.9rem !important", color: "#8b5cf6 !important" }} />}
          label="Sleeping"
          sx={{
            height: 22,
            fontSize: "0.7rem",
            fontWeight: 500,
            background: "rgba(139, 92, 246, 0.12)",
            color: "#8b5cf6",
            border: "none",
            cursor: "pointer",
          }}
        />
      );
    }

    return (
      <Chip
        size="small"
        label="No Service"
        sx={{
          height: 22,
          fontSize: "0.7rem",
          fontWeight: 500,
          background: "rgba(100, 116, 139, 0.12)",
          color: "#64748b",
          border: "none",
        }}
      />
    );
  };

  // Only show tooltip if there's something meaningful to show
  if (status === "stopped" || !serviceName) {
    return getChip();
  }

  return (
    <Tooltip
      title={<RuntimeTooltipContent pods={pods} latestImage={latestImage} serviceName={serviceName} />}
      arrow
      placement="top"
    >
      {getChip()}
    </Tooltip>
  );
};

/**
 * Format memory for display
 */
const formatMemory = (bytes) => {
  if (!bytes) return "0 MB";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(1)} GB`;
  }
  return `${Math.round(mb)} MB`;
};

/**
 * Row params generator for unified table
 */
const getRowsParams = (apps) => ({
  header: [
    "Name",
    "Build",
    "Runtime",
    "Pods",
    "CPU",
    "Memory",
    "URL",
    "Git",
  ],
  body: apps,
  bodyCount: apps,

  bodyParams: (app) => [
    {
      value: app.displayName || app.name,
    },
    {
      value: (
        <BuildStatusChip
          status={app.build?.status === "building" ? "False" : app.build?.status === "ready" ? "True" : "False"}
          reason={app.build?.reason}
          buildCounter={app.build?.buildCounter}
          latestBuildReason={app.build?.latestBuildReason}
          message={app.build?.message}
        />
      ),
    },
    {
      value: (
        <RuntimeStatusChip
          status={app.runtime?.status}
          pods={app.runtime?.pods}
          latestImage={app.build?.latestImage}
          serviceName={app.runtime?.serviceName}
        />
      ),
    },
    {
      value: app.runtime?.pods || 0,
    },
    {
      value: app.runtime?.cpu
        ? `${Math.round((app.runtime.cpu + Number.EPSILON) * 100) / 100}%`
        : "0%",
    },
    {
      value: formatMemory(app.runtime?.memory),
    },
    {
      value: app.runtime?.url || "-",
      specialDetails: app.runtime?.url ? {
        hasTooltip: true,
        isUrl: true,
        ellipsis: { maxWidth: "10em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
      } : {},
    },
    {
      value: app.build?.gitUrl || "-",
      specialDetails: app.build?.gitUrl ? {
        hasTooltip: true,
        isUrl: true,
        ellipsis: { maxWidth: "10em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
      } : {},
    },
  ],
});

/**
 * Table view for unified apps - wraps existing Table component
 */
export function TableView({
  apps,
  loading,
  page,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  onRowClick,
  searchedValue,
  onChangeSearchTxt,
  onKeyPressSearch,
  onClearSearchTxt,
}) {
  const paginatedApps = apps.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Table
      rows={{
        ...getRowsParams(apps),
        body: paginatedApps,
      }}
      selectedRow={() => false}
      onRowClick={onRowClick}
      page={page}
      rowsPerPage={rowsPerPage}
      onChangePage={onChangePage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      isLoading={loading}
      searchedValue={searchedValue}
      onChangeSearchTxt={onChangeSearchTxt}
      onKeyPressSearch={onKeyPressSearch}
      onClearSearchTxt={onClearSearchTxt}
    />
  );
}

export default TableView;
